import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId.js';
import { hasAtLeastOneField } from '../shared/refines';
import { CouponResponseValidationSchema } from './response.js';
import {
  CouponRuleInputValidationSchema,
  couponPageIdsDescription,
  couponRulesDescription,
  couponTicketTypeIdsDescription,
  rulesWithoutLegacyScope,
} from './scopeDescriptions.js';

const couponUpdateBodyShape = {
  label: z.string().min(1, 'Label cannot be an empty string.').optional().meta({
    description: 'Internal name for the coupon.',
    example: 'Free shipping within US',
  }),
  deleted: z.boolean().optional().meta({
    description:
      'Set true to soft delete the coupon. It stops working at checkout but its data and redemption history are retained.',
    example: false,
  }),
  rules: z
    .array(CouponRuleInputValidationSchema)
    .optional()
    .meta({
      description: `${couponRulesDescription} Replaces every stored rule; an empty list makes the coupon work everywhere.`,
      example: [{ pageId: '67ee075004de439ab0b675b6', appliesTo: 'wholeOrder' }],
    }),
  pageIds: z
    .array(ObjectIdStringValidationSchema)
    .optional()
    .meta({
      description: `${couponPageIdsDescription} Replaces the stored rules with whole-order rules for these pages; an empty array makes the coupon work everywhere. Refused with a 400 when the coupon's rules go beyond one \`wholeOrder\` rule, or one \`tickets\` rule with picked \`ticketTypeIds\`, per page: send \`rules\` instead.`,
      deprecated: true,
      example: ['67ee075004de439ab0b675b6', '67fcbdac6a91c25ef2d3534a'],
    }),
  ticketTypeIds: z
    .array(ObjectIdStringValidationSchema)
    .optional()
    .meta({
      description: `${couponTicketTypeIdsDescription} Without \`pageIds\`, checked against the pages already on the coupon. Refused with a 400 when the coupon's rules go beyond one \`wholeOrder\` rule, or one \`tickets\` rule with picked \`ticketTypeIds\`, per page.`,
      deprecated: true,
      example: ['67ee075004de439ab0b675b6', '67fcbdac6a91c25ef2d3534a'],
    }),
  maxRedemptions: z.number().min(0).nullable().optional().meta({
    description:
      "The total number of times this coupon can be redeemed across all customers. Doesn't limit a single customer from redeeming multiple times. Send `null` to remove the limit.",
    example: 5,
  }),
  redeemBy: z.iso.datetime().nullable().optional().meta({
    description:
      'The ISO datetime that this coupon must be used before. Send `null` to remove the expiry.',
    example: '2025-10-04T07:36:45.122Z',
  }),
};

/**
 * Exported so the MCP tool can iterate the same field list instead of
 * re-declaring it (drift-proof).
 */
export const COUPON_UPDATE_FIELDS = Object.keys(couponUpdateBodyShape) as ReadonlyArray<
  keyof typeof couponUpdateBodyShape
>;

export const UpdateCouponInputValidationSchema = z
  .object({
    couponId: ObjectIdStringValidationSchema.meta({
      description:
        'The ID of the coupon to update. Use `list_coupons` first if you need to discover the coupon ID by code or label.',
    }),
    ...couponUpdateBodyShape,
  })
  .refine(
    value => {
      const { couponId: _couponId, ...rest } = value;
      return hasAtLeastOneField(rest);
    },
    { message: 'At least one field to update must be provided.' },
  )
  .refine(rulesWithoutLegacyScope.check, {
    message: rulesWithoutLegacyScope.message,
    path: ['rules'],
  });

export const UpdateCouponResponseValidationSchema = z.object({
  data: CouponResponseValidationSchema,
});
